"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Portfolio/PortfolioGhostState/LinkAccountBar";

import React from "react";
import { PORTFOLIO_GHOST_LINK_BAR_HEIGHT_PX } from "./linkAccountBarTokens";

import { cn } from "utils";
import { Icon } from "@ospweb/sdk-ui";

const I18n = I18nUtil.createScoped(Translations);

export type LinkAccountBarLayout = "inline" | "h5-pinned" | "pc-bottom";
export type LinkAccountBarVariant = "no_account" | "no_invest_account";
/** Invest Portfolio vs Track·My Money / Dashboard 未连接标题 */
export type LinkAccountBarSurface = "invest" | "my_money";

export interface LinkAccountBarProps {
  variant: LinkAccountBarVariant;
  onImportClick?: () => void;
  onUploadClick?: () => void;
  onDashboardClick?: () => void;
  className?: string;
  layout?: LinkAccountBarLayout;
  /**
   * `invest` →「Link your invest account」；`my_money` →「Link your account」（Figma UI1'）。
   * 仅影响 `no_account` 标题；默认 invest。
   */
  surface?: LinkAccountBarSurface;
  /** Figma `102169:37541` FreeBadge；默认展示 */
  showFreeTrialBadge?: boolean;
}

/**
 * V3.0.4 Annotation `102169:37529` — 副文案待补 translate（目录只读）。
 */
const NO_ACCOUNT_DESCRIPTION =
  "Connect your accounts and I'll keep an eye on your net worth, spending, and what's coming up.";

/** V3.0.4 Annotation — CTA 文案「Connect account」（translate 只读，英文字面） */
const CONNECT_ACCOUNT_LABEL = "Connect account";

/** Figma `102169:37541` FreeBadge 文案 */
const FREE_FOR_30_DAYS_LABEL = "Free for 30 days";

/** Track·My Money / Dashboard UI1'（translate 只读，英文字面） */
const LINK_YOUR_ACCOUNT_TITLE = "Link your account";

const VARIANT_COPY: Record<
  LinkAccountBarVariant,
  { title: string; description: string }
> = {
  no_account: {
    title: I18n.t("Link your invest account"),
    description: NO_ACCOUNT_DESCRIPTION,
  },
  no_invest_account: {
    title: I18n.t("No investment accounts yet"),
    description: I18n.t(
      "Add a brokerage, 401(k) or wallet and your holdings replace this preview.",
    ),
  },
};

/**
 * Primary CTA for unlinked portfolio ghost state.
 * H5/PC share the same brand banner (Figma `102169:37529` UpgradeCard / legacy ghost nodes).
 * variant drives copy; `no_invest_account` adds a Dashboard navigation link.
 *
 * V3.0.4 Annotations:
 * - Connect Plaid → Connect account；移除 Plaid logo
 * - 右上角 Free for 30 days 角标
 */
export const LinkAccountBar: React.FC<LinkAccountBarProps> = ({
  variant,
  onImportClick,
  onUploadClick,
  onDashboardClick,
  className,
  layout = "h5-pinned",
  surface = "invest",
  showFreeTrialBadge = true,
}) => {
  const baseCopy = VARIANT_COPY[variant];
  const copy =
    variant === "no_account" && surface === "my_money"
      ? { ...baseCopy, title: LINK_YOUR_ACCOUNT_TITLE }
      : baseCopy;

  const content = (
    <div
      className={cn(
        "relative flex flex-col gap-3 overflow-hidden rounded-xl bg-background_brand_overlay100 p-4 md:w-480px md:h-fit md:p-8 md:gap-6",
        className,
      )}
      data-testid="portfolio-ghost-link-bar"
      data-figma-node={
        surface === "my_money" ? "102046:406790" : "102169:37529"
      }
      data-link-account-surface={surface}
    >
      {showFreeTrialBadge ? (
        <div
          data-testid="portfolio-ghost-free-trial-badge"
          data-figma-node="102169:37541"
          className={cn(
            "absolute right-0 top-0 z-1 flex items-center overflow-hidden",
            "rounded-bl-3 rounded-tr-xl border-[3px] border-border_brand_solid",
            "bg-background_auralv3_overlay100 px-2.5 py-1",
          )}
        >
          <p className="body_bold_s text-content_primary whitespace-nowrap">
            {FREE_FOR_30_DAYS_LABEL}
          </p>
        </div>
      ) : null}
      <div className="flex min-w-0 flex-col gap-1 md:gap-3">
        <p className="title_l text-content_inverse_primary md:headline_regular_s">
          {copy.title}
        </p>
        <p className="body_m text-content_inverse_secondary md:body_l">
          {copy.description}
        </p>
      </div>
      {/* flex-wrap：Dashboard lock `max-w-[361px]` / 窄屏时避免 overflow-hidden 裁切第二颗 CTA */}
      <div className="flex w-full min-w-0 flex-row flex-wrap items-center gap-3.5">
        <button
          type="button"
          aria-label={CONNECT_ACCOUNT_LABEL}
          data-testid="portfolio-ghost-link-account"
          data-openreplay-label="Portfolio · Connect account"
          className="buttton sm_button md:lg_button w-fit shrink-0 bg-black button_m md:button_l text-content_inverse_primary"
          onClick={onImportClick}
        >
          {CONNECT_ACCOUNT_LABEL}
        </button>
        <button
          type="button"
          aria-label="Send Ed a screenshot"
          data-testid="portfolio-ghost-upload-screenshot"
          className="buttton sm_button md:lg_button w-fit shrink-0 inverse_button button_m md:button_l text-content_primary"
          onClick={onUploadClick}
        >
          {I18n.t("Send Ed a screenshot")}
        </button>
      </div>
      {variant === "no_invest_account" && (
        <button
          type="button"
          data-testid="portfolio-ghost-dashboard-link"
          className="self-start button_m text-content_inverse_primary flex"
          onClick={onDashboardClick}
        >
          {I18n.t("Your accounts feed your dashboard")}
          <Icon name="right-arrow-alt" size={20} />
        </button>
      )}
    </div>
  );

  if (layout === "inline") {
    return content;
  }

  if (layout === "pc-bottom") {
    return (
      <div
        className="absolute inset-0 w-full shrink-0 flex justify-center items-center"
        data-testid="portfolio-ghost-link-sticky-pc"
      >
        {content}
      </div>
    );
  }

  return (
    <>
      <div
        aria-hidden
        data-testid="portfolio-ghost-link-spacer-h5"
        style={{
          height: `calc(${PORTFOLIO_GHOST_LINK_BAR_HEIGHT_PX}px + var(--mobile-bottom-height, 116px))`,
        }}
      />
      <div
        className="fixed left-0 right-0 z-[810] px-4"
        data-testid="portfolio-ghost-link-pinned-h5"
        style={{
          bottom: "var(--mobile-bottom-height, 116px)",
        }}
      >
        {content}
      </div>
    </>
  );
};
